import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { reportClientCrash } from "@/lib/crashReport";

// Lot 543 — verrou de défilement du <body> PARTAGÉ par les écrans plein écran (éditeur de story, composeur téléphone,
// caméra intégrée, galerie). Avant, chacun posait `overflow: hidden` et « restaurait la valeur d'avant » : quand un parent
// et la caméra se démontaient dans le même rendu (changement de page caméra ouverte), React nettoyait le parent PUIS la
// caméra, qui remettait… `hidden` → plus aucun défilement dans toute l'app jusqu'à sa fermeture (gel observé en 2.0.2).
// Ici : compteur — le body n'est libéré que quand le DERNIER écran se ferme, quel que soit l'ordre.
// Lot 755 — le verrou posé par Yalola est MARQUÉ (`data-yalola-scroll-lock`) : un `overflow: hidden` posé sur le body par
// un script tiers (vignette / ancre AdSense, message de consentement Google, extension) n'est ni libéré ni signalé — le
// garde-fou ne juge que SES verrous. Avant, sur le web, chaque vignette AdSense à l'ouverture d'une publication était
// « libérée » (le fond redevenait défilable derrière la pub) et consignée comme « verrou orphelin » (un rapport par appareil).
let locks = 0;
const MARK = "data-yalola-scroll-lock";

export const lockBodyScroll = () => {
  if (locks === 0) { document.body.style.overflow = "hidden"; document.body.setAttribute(MARK, "1"); }
  locks += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    locks = Math.max(0, locks - 1);
    if (locks === 0) { document.body.style.overflow = ""; document.body.removeAttribute(MARK); }
  };
};

export const bodyScrollLocks = () => locks;

export const useBodyScrollLock = (active = true) => {
  useEffect(() => (active ? lockBodyScroll() : undefined), [active]);
};

// Indices pour le rapport : qui d'autre tient le body ? (vignette AdSense, consentement Google, verrou Radix)
const orphanHints = () => {
  const h = [];
  if (document.body.getAttribute("data-scroll-locked")) h.push("radix");
  if (document.querySelector("ins.adsbygoogle[data-vignette-loaded], .google-auto-placed, #google_vignette, iframe[id^='google_ads_iframe']")) h.push("adsense");
  if (window.location.hash.includes("google_vignette")) h.push("vignette");
  if (document.querySelector(".fc-ab-root, .fc-consent-root, #fc-dialog")) h.push("consent");
  if (document.querySelector("[data-testid^='lightbox'], [data-testid='story-camera'], [data-testid='post-fullscreen-composer']")) h.push("fullscreen-mounted");
  return h;
};

// Garde-fou : à chaque changement de page, un verrou YALOLA (marqué) encore posé sur le body sans aucun écran plein écran
// monté est un verrou orphelin → libéré et consigné (Admin › Supervision, kind scroll_lock) pour en garder la preuve.
// Re-vérifié 1 s plus tard : un verrou qui n'existe plus à ce moment-là (écran en cours de montage) n'est pas signalé.
export const isOrphanBodyLock = () => locks === 0 && document.body.style.overflow === "hidden" && document.body.getAttribute(MARK) === "1";

export const ScrollLockGuard = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    let second = null;
    const id = setTimeout(() => {
      if (!isOrphanBodyLock()) return;
      second = setTimeout(() => {
        if (!isOrphanBodyLock()) return;
        document.body.style.overflow = "";
        document.body.removeAttribute(MARK);
        const hints = orphanHints();
        reportClientCrash({ error: new Error(`verrou de défilement orphelin libéré sur ${pathname}${hints.length ? ` [${hints.join(",")}]` : ""}`), kind: "scroll_lock" });
      }, 1000);
    }, 300);
    return () => { clearTimeout(id); if (second) clearTimeout(second); };
  }, [pathname]);
  return null;
};
